import { request } from './client';

export interface Project {
  wbs1: string;
  wbs1Name: string;
  chargeType: string;
  status: string;
  readyForProcessing: string;
  // VP SubLevel=="Y" — time must charge to a WBS2/WBS3 leaf, not the summary row.
  // Sourced from the BE ProjectContract (ADR-0028 amendment 2026-06-16).
  isPhased?: boolean;
}

export interface ProjectSearchResponse {
  projects: Project[];
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export function searchProjects(
  q: string,
  page = 1,
  pageSize = 20,
): Promise<ProjectSearchResponse> {
  const params = new URLSearchParams({ q, page: String(page), pageSize: String(pageSize) });
  return request<ProjectSearchResponse>(`/api/projects/search?${params}`);
}

export interface Phase {
  wbs2: string;
  name: string;
  chargeType: string;
  status: string;
}

export interface PhaseSearchResponse {
  phases: Phase[];
  hasMore: boolean;
}

export interface Task {
  wbs3: string;
  name: string;
  chargeType: string;
  status: string;
}

export interface TaskSearchResponse {
  tasks: Task[];
  hasMore: boolean;
}

// VP pages its WBS lookups and the picker needs the whole list: a phase the user
// cannot scroll to is a phase they cannot charge to (#6550). Stops on the
// server's hasMore, an empty page, or a page that adds nothing new — the last of
// which is what a VP that ignores `page` looks like, and is why this cannot spin.
async function collectPages<T>(
  fetchPage: (page: number) => Promise<{ items: T[]; hasMore: boolean }>,
  key: (item: T) => string,
): Promise<T[]> {
  const byKey = new Map<string, T>();
  for (let page = 1; ; page += 1) {
    const { items, hasMore } = await fetchPage(page);
    const before = byKey.size;
    for (const item of items) byKey.set(key(item), item);
    if (!hasMore || items.length === 0 || byKey.size === before) return [...byKey.values()];
  }
}

export async function getPhases(wbs1: string): Promise<PhaseSearchResponse> {
  const phases = await collectPages<Phase>(
    async (page) => {
      const res = await request<PhaseSearchResponse>(
        `/api/projects/${encodeURIComponent(wbs1)}/phases?page=${page}`);
      return { items: res.phases, hasMore: res.hasMore };
    },
    (p) => p.wbs2,
  );
  return { phases, hasMore: false };
}

export async function getTasks(wbs1: string, wbs2: string): Promise<TaskSearchResponse> {
  const tasks = await collectPages<Task>(
    async (page) => {
      const res = await request<TaskSearchResponse>(
        `/api/projects/${encodeURIComponent(wbs1)}/phases/${encodeURIComponent(wbs2)}/tasks?page=${page}`);
      return { items: res.tasks, hasMore: res.hasMore };
    },
    (t) => t.wbs3,
  );
  return { tasks, hasMore: false };
}
